import React, { useState } from 'react';
import { TrendingUp, Clock, DollarSign, AlertCircle, CheckCircle2, Target, Calculator } from 'lucide-react';

const ROICalculator = ({ industry = 'plumber' }) => {
  // Industry-specific realistic defaults
  const industryDefaults = {
    plumber: {
      avgJobValue: 385,
      missedCallsPerWeek: 8,
      quoteConversionRate: 28,
      hoursOnAdminPerWeek: 12,
      hourlyRate: 35
    },
    hvac: {
      avgJobValue: 720,
      missedCallsPerWeek: 12,
      quoteConversionRate: 22,
      hoursOnAdminPerWeek: 15,
      hourlyRate: 40
    },
    roofing: {
      avgJobValue: 5200,
      missedCallsPerWeek: 6,
      quoteConversionRate: 18,
      hoursOnAdminPerWeek: 10,
      hourlyRate: 45
    },
    dental: {
      avgJobValue: 320,
      missedCallsPerWeek: 18,
      quoteConversionRate: 35,
      hoursOnAdminPerWeek: 16,
      hourlyRate: 55
    }
  };

  const [inputs, setInputs] = useState(industryDefaults[industry] || industryDefaults.plumber);
  const [results, setResults] = useState(null);

  const calculateROI = () => {
    // Conservative, realistic calculations
    const missedCallsPerMonth = inputs.missedCallsPerWeek * 4.33;
    const currentConversionRate = inputs.quoteConversionRate / 100;
    
    // Lost revenue: Only 25% of missed calls would have converted (conservative)
    const lostJobsPerMonth = missedCallsPerMonth * 0.25;
    const lostRevenuePerMonth = lostJobsPerMonth * inputs.avgJobValue;
    
    // Time costs
    const hoursWastedPerMonth = inputs.hoursOnAdminPerWeek * 4.33;
    const timeValueLostPerMonth = hoursWastedPerMonth * inputs.hourlyRate;
    
    // With system: Realistic improvements
    // - System optionally answers 85% of calls when you can't (15% still miss due to network/other issues)
    // - Or sends automatic preset replies
    // - Conversion improves by 60% (not doubles, realistic)
    const callsCaptured = missedCallsPerMonth * 0.85;
    const improvedConversionRate = Math.min(currentConversionRate * 1.6, 0.65); // Max 65%
    const additionalJobsWithImprovement = callsCaptured * improvedConversionRate;
    const additionalRevenuePerMonth = additionalJobsWithImprovement * inputs.avgJobValue;
    
    // Time saved: System automates 65% of admin work (realistic, not 80%)
    const hoursSavedPerMonth = hoursWastedPerMonth * 0.65;
    const timeValueSavedPerMonth = hoursSavedPerMonth * inputs.hourlyRate;
    
    // Total gains
    const totalMonthlyGain = additionalRevenuePerMonth + timeValueSavedPerMonth;
    const systemCost = 497; // Growth to Domination upgrade price
    const netMonthlyProfit = totalMonthlyGain - systemCost;
    const roi = ((totalMonthlyGain - systemCost) / systemCost) * 100;
    const paybackDays = (systemCost / (totalMonthlyGain / 30)).toFixed(1);
    
    // Annual calculations
    const annualGain = totalMonthlyGain * 12;
    const annualSystemCost = systemCost * 12;
    const annualNetProfit = netMonthlyProfit * 12;

    setResults({
      lostRevenuePerMonth: Math.round(lostRevenuePerMonth),
      timeValueLostPerMonth: Math.round(timeValueLostPerMonth),
      additionalRevenuePerMonth: Math.round(additionalRevenuePerMonth),
      timeValueSavedPerMonth: Math.round(timeValueSavedPerMonth),
      totalMonthlyGain: Math.round(totalMonthlyGain),
      netMonthlyProfit: Math.round(netMonthlyProfit),
      roi: Math.round(roi),
      paybackDays,
      hoursSavedPerMonth: Math.round(hoursSavedPerMonth),
      annualGain: Math.round(annualGain),
      annualSystemCost,
      annualNetProfit: Math.round(annualNetProfit),
      additionalJobs: Math.round(additionalJobsWithImprovement)
    });
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  return (
    <div className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-2xl p-8 border-2 border-blue-500/30">
      
      {/* Emotional Hook Header */}
      <div className="text-center mb-8">
        <div className="inline-block bg-yellow-500/20 border border-yellow-500 rounded-full px-6 py-3 mb-4">
          <p className="text-yellow-400 font-bold text-lg">
            💡 Remember Why You Started Your Business?
          </p>
        </div>
        <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
          It Wasn't To Work 80-Hour Weeks<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
            And Miss Your Kid's Football Games
          </span>
        </h3>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          Calculate exactly how much money and time you're losing right now.<br />
          <strong className="text-white">This isn't an expense. It's an investment that pays YOU.</strong>
        </p>
      </div>

      {/* Single Column Layout */}
      <div className="max-w-3xl mx-auto">
        
        {/* Input Section */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 mb-6">
          <h4 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Target className="mr-3 text-blue-400" size={28} />
            Your Numbers (Be Honest)
          </h4>
          
          <div className="space-y-5">
            <div>
              <label className="block text-gray-300 mb-2 font-medium">
                Average Job Value (£)
              </label>
              <input
                type="number"
                value={inputs.avgJobValue}
                onChange={(e) => handleInputChange('avgJobValue', e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-bold focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">
                Calls You Miss Per Week
              </label>
              <input
                type="number"
                value={inputs.missedCallsPerWeek}
                onChange={(e) => handleInputChange('missedCallsPerWeek', e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-bold focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">Most businesses miss 6-15 calls per week</p>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">
                Current Quote Conversion Rate (%)
              </label>
              <input
                type="number"
                value={inputs.quoteConversionRate}
                onChange={(e) => handleInputChange('quoteConversionRate', e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-bold focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">How many quotes turn into jobs?</p>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">
                Hours on Admin/Scheduling Weekly
              </label>
              <input
                type="number"
                value={inputs.hoursOnAdminPerWeek}
                onChange={(e) => handleInputChange('hoursOnAdminPerWeek', e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-bold focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">Calling back, scheduling, follow-ups, etc.</p>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">
                What's Your Time Worth Per Hour? (£)
              </label>
              <input
                type="number"
                value={inputs.hourlyRate}
                onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-bold focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">What you could charge if you were working</p>
            </div>

            {/* Calculate Button - IRRESISTIBLE */}
            <button
              onClick={calculateROI}
              className="w-full bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 hover:from-green-700 hover:via-emerald-700 hover:to-green-700 text-white text-2xl font-black py-6 px-8 rounded-xl shadow-2xl transform hover:scale-105 transition-all flex items-center justify-center border-4 border-green-400 animate-pulse"
            >
              <Calculator className="mr-3" size={32} />
              CALCULATE MY REAL NUMBERS NOW
            </button>
            <p className="text-center text-green-400 font-bold mt-2 text-sm">
              ⬆️ Click to see exactly how much you're losing right now ⬆️
            </p>
          </div>
        </div>

        {/* Results Section - Only show after calculation */}
        {results && (
          <div className="space-y-6">
          
          {/* The Brutal Truth */}
          <div className="bg-red-900/30 border-2 border-red-500 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="text-red-400 mr-3" size={28} />
              <h4 className="text-2xl font-bold text-white">The Brutal Truth</h4>
            </div>
            
            <div className="space-y-3">
              <div className="bg-red-800/30 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Lost Revenue (Missed Calls)</p>
                <p className="text-3xl font-black text-red-400">
                  £{results.lostRevenuePerMonth?.toLocaleString()}<span className="text-lg">/month</span>
                </p>
              </div>

              <div className="bg-red-800/30 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Your Time Wasted</p>
                <p className="text-3xl font-black text-red-400">
                  £{results.timeValueLostPerMonth?.toLocaleString()}<span className="text-lg">/month</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">{Math.round(inputs.hoursOnAdminPerWeek * 4.33)} hours/month doing admin work</p>
              </div>

              <div className="border-t-2 border-red-500/50 pt-3 mt-3">
                <p className="text-gray-300 text-sm mb-1">Total You're Losing Right Now</p>
                <p className="text-4xl font-black text-red-400">
                  £{(results.lostRevenuePerMonth + results.timeValueLostPerMonth)?.toLocaleString()}
                </p>
                <p className="text-xl text-red-300 mt-2">
                  £{((results.lostRevenuePerMonth + results.timeValueLostPerMonth) * 12)?.toLocaleString()}/year
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-red-800/20 rounded-lg">
              <p className="text-sm text-gray-300 italic">
                "I'll save money by NOT investing in my business" ← This is costing you everything.
              </p>
            </div>
          </div>

          {/* With The System */}
          <div className="bg-green-900/30 border-2 border-green-500 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <TrendingUp className="text-green-400 mr-3" size={28} />
              <h4 className="text-2xl font-bold text-white">With Our System</h4>
            </div>
            
            <div className="space-y-3">
              <div className="bg-green-800/30 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Additional Revenue (Captured Calls)</p>
                <p className="text-3xl font-black text-green-400">
                  +£{results.additionalRevenuePerMonth?.toLocaleString()}<span className="text-lg">/month</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">~{results.additionalJobs} more jobs per month (AI answers when you can't or sends preset replies)</p>
              </div>

              <div className="bg-green-800/30 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Your Time Back (80% Automated)</p>
                <p className="text-3xl font-black text-green-400">
                  +£{results.timeValueSavedPerMonth?.toLocaleString()}<span className="text-lg">/month</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">{results.hoursSavedPerMonth} hours/month freed up</p>
              </div>

              <div className="border-t-2 border-green-500/50 pt-3 mt-3">
                <p className="text-gray-300 text-sm mb-1">Total Monthly Gain</p>
                <p className="text-4xl font-black text-green-400">
                  £{results.totalMonthlyGain?.toLocaleString()}
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                <p className="text-gray-300 text-sm mb-1">Minus System Cost</p>
                <p className="text-2xl font-bold text-white line-through opacity-50">
                  -£497<span className="text-sm">/month</span>
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-4">
                <p className="text-white text-sm mb-1 font-bold">YOUR NET PROFIT</p>
                <p className="text-5xl font-black text-white">
                  £{results.netMonthlyProfit?.toLocaleString()}
                </p>
                <p className="text-xl text-green-100 mt-2">
                  £{results.annualNetProfit?.toLocaleString()}/year in your pocket
                </p>
              </div>
            </div>
          </div>

          {/* ROI Metrics */}
          <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border-2 border-yellow-500 rounded-xl p-6">
            <h4 className="text-2xl font-bold text-white mb-4 flex items-center">
              <DollarSign className="mr-2 text-yellow-400" size={28} />
              The Numbers Don't Lie
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/30 rounded-lg p-4 text-center">
                <p className="text-gray-300 text-xs mb-2">ROI</p>
                <p className="text-4xl font-black text-yellow-400">{results.roi}%</p>
              </div>

              <div className="bg-black/30 rounded-lg p-4 text-center">
                <p className="text-gray-300 text-xs mb-2">Payback Time</p>
                <p className="text-4xl font-black text-yellow-400">{results.paybackDays}</p>
                <p className="text-xs text-gray-400">days</p>
              </div>

              <div className="bg-black/30 rounded-lg p-4 text-center col-span-2">
                <p className="text-gray-300 text-xs mb-2">Time Back Per Year</p>
                <p className="text-4xl font-black text-yellow-400">{Math.round(results.hoursSavedPerMonth * 12)}</p>
                <p className="text-xs text-gray-400">hours = {Math.round(results.hoursSavedPerMonth * 12 / 40)} work weeks</p>
              </div>
            </div>
          </div>

          {/* Emotional Close */}
          <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-2 border-purple-500 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <Clock className="text-purple-400 mr-3" size={28} />
              <h4 className="text-2xl font-bold text-white">What Could You Do With That Time?</h4>
            </div>
            
            <div className="space-y-2 text-gray-300">
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Be there for your child's school play, nativity, sports day - ALL of them</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Actually tuck your kids in at night instead of getting home after they're asleep</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Take a proper family holiday without checking your phone every 5 minutes</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Have dinner with your family EVERY night - whilst they're still young</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Watch your kids grow up in real-time, not through photos you barely had time to take</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Sleep through the night without worrying about missed opportunities</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-green-400 mr-2 mt-1 flex-shrink-0" size={20} />
                <p>Work ON your business instead of being trapped IN it</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-purple-900/40 rounded-lg border border-purple-500/30">
              <p className="text-white font-bold text-center text-lg">
                This isn't about spending £497/month.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 text-2xl">
                  It's about earning an extra £{results.netMonthlyProfit?.toLocaleString()}/month while getting your life back.
                </span>
              </p>
            </div>
          </div>

        </div>
        )}

      </div>

      {/* Bottom CTA - Always visible */}
      <div className="mt-8 text-center bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-6 border border-blue-500/30">
        <p className="text-xl text-gray-300">
          {results ? (
            <>
              You're already losing <strong className="text-red-400">£{(results.lostRevenuePerMonth + results.timeValueLostPerMonth)?.toLocaleString()}/month</strong> by doing nothing.<br />
              <span className="text-blue-400 font-bold">The question isn't "Can I afford this?"</span><br />
              <span className="text-white text-2xl font-black">The question is: "Can I afford NOT to do this?"</span>
            </>
          ) : (
            <>
              <span className="text-white text-xl font-bold">Enter your real numbers above and hit calculate.</span><br />
              <span className="text-gray-400">You'll see exactly how much money you're leaving on the table right now.</span>
            </>
          )}
        </p>
      </div>

    </div>
  );
};

export default ROICalculator;
